// HOW LONG A CLIENT WAITED FOR SOMEONE AT YWS — the mock's waits.py, ported.
//
// ⭐ PURE: messages and people in, waits out. lib/server/client-slack-response-
// times.ts reads the rows; this decides what a wait is, and nothing here knows
// about brands, windows or pages.
//
// THE RULE, as the signed-off mock's waits.py has it:
//
//   - A WAIT opens at the first client message in a thread with no YWS reply
//     after it. Several client messages in a row are ONE wait.
//   - It ends at the first YWS person's message in that thread, or, when the
//     thread never gets one, at a later TOP-LEVEL YWS message in the channel
//     that @-mentions one of the waiting client's people.
//   - Reactions never end a wait (a 👍 is not a reply), but are kept: `acked`.
//   - A client message older than `since` opens nothing: it is an old thread's
//     root, read for context (a reply to it is still read).
//   - Bots, and the subtypes Slack uses for joins, topics and the like, are not
//     people talking, and are left out (`SKIP`).
//
// "NEEDS A REPLY" HERE IS THE RULES' ANSWER: a wait made only of short
// acknowledgements ("thanks", "sounds good") needs nothing. The rules still let
// some of those through ("All good on our side, cheers" is five words neither
// pattern knows, so it counts). The model's read (lib/server/client-slack-
// triage.ts) decides instead wherever it has read the wait as it stands;
// `needs` is the fallback for a wait it has not, and the page says how many
// those are.
//
// ⚠️ A THREAD'S KEY is its root's ts, and a lone message is its own thread: a
// root has thread_ts = ts, a reply thread_ts = its root, a lone message NULL.
// A reply also sent to the channel (`thread_broadcast`) is ONE row and a
// reply, so it is in its thread and never a top-level message.
//
// Slack's ts strings are compared as numbers here: every one in a channel has
// the same ten digits before the point, so the order is the text's order too.

/** The subtypes that are Slack talking about the channel, not a person in it (waits.py's SKIP). */
const SKIP = new Set([
  "channel_join",
  "channel_leave",
  "channel_purpose",
  "channel_topic",
  "channel_name",
  "bot_message",
  "pinned_item",
  "channel_archive",
  "reminder_add",
  "huddle_thread",
]);
/** The same list, for a reader that asks it in SQL (the re-check's "conversations since"). */
export const NOT_SPOKEN_SUBTYPES: readonly string[] = Array.from(SKIP);

// waits.py's two patterns, verbatim but for the flags: `u` so the emoji in the
// tail class are one character each, `i` for re.I. Python's \w also matches
// accented letters where JavaScript's does not; a client writing "merci" with
// an accent is one more word, which only ever moves a message from ack to other.
const ACK_WORDS =
  "ok(ay)?|k|thanks?( (so much|you|a lot))?|thank you( so much)?|thx|ty|great|perfect|sounds good|awesome|got it|will do|noted|that'?s (ok(ay)?|fine|great)|that should be fine|love (it|this)|yes|yep|yeah|cool|nice|amazing|received|done|appreciate it|good|sure|no worries|lol|haha";
const ACK = new RegExp(`^\\W*(${ACK_WORDS})\\b[\\w\\s!.,:;)(❤️🙏👍-]*$`, "iu");
/**
 * The acknowledgement words alone, at the start (ACK's head, without its
 * tail): the hint the inbox's set-aside selector reads (lib/client-slack-
 * setaside.ts `plainAck`). ACK's tail takes any words after
 * them, so "thanks" can open a delivery, an update or a new ask.
 */
export const ACK_HEAD = new RegExp(`^\\W*(${ACK_WORDS})\\b`, "iu");
const ASK =
  /\?|\b(can you|could you|would you|please|pls|plz|let me know|lmk|following up|follow up|any update|waiting|need|still|when will|confirm|send|share|asap|urgent)\b/i;

export type MessageKind = "ask" | "ack" | "other";

/**
 * What one client message is, by the rules: an ask (a question mark, or a
 * request's words), an acknowledgement (a short thanks or okay, or three words
 * or fewer), or neither. waits.py's `kind`.
 */
export function messageKind(text: string): MessageKind {
  if (ASK.test(text)) return "ask";
  const t = text.trim();
  if (ACK.test(t) || t.split(/\s+/).filter(Boolean).length <= 3) return "ack";
  return "other";
}

/** One stored message, as the rules need it (a `client_slack_message` row). */
export interface WaitMessage {
  ts: string;
  /** NULL for a lone message; equal to `ts` for a thread's root; the root's ts for a reply. */
  threadTs: string | null;
  userId: string | null;
  botId: string | null;
  subtype: string | null;
  text: string;
  reactions?: ReadonlyArray<{ name: string; users?: readonly string[] }> | null;
}

/** A Slack person, as the rules need them (`client_slack_person`). Unknown: a client, not a bot. */
export interface WaitPerson {
  yws: boolean;
  bot: boolean;
}

export interface Wait {
  channelId: string;
  /** The thread's key: its root's ts, or a lone message's own. */
  threadTs: string;
  /** The first waiting message's ts, in seconds. */
  start: number;
  /** When someone at YWS answered, in seconds; null while nobody has. */
  end: number | null;
  /** Who answered (a Slack user id), and how: in the thread, or by @-mentioning them in the channel. */
  answeredBy: string | null;
  endedBy: "thread" | "mention" | null;
  /** The waiting messages' ts, oldest first. */
  anchors: string[];
  /** The client people who wrote them. */
  authors: string[];
  /** The rules' read of each message, in order. */
  kinds: MessageKind[];
  /** Needs a reply, by the rules: anything but acknowledgements only. */
  needs: boolean;
  /** Someone at YWS reacted to one of the waiting messages. Never ends the wait. */
  acked: boolean;
}

const MENTION = /<@(\w+)>/g;
const mentionsOf = (text: string): Set<string> => new Set(Array.from(text.matchAll(MENTION), (m) => m[1]));
const secs = (ts: string): number => Number(ts);

/**
 * Every wait in one channel. `messages` is the channel's stored messages, in
 * any order (deleted ones already left out: Slack no longer shows them);
 * `since` is in seconds.
 */
/**
 * A person talking, as the rule counts them: not a bot's post, not one of
 * Slack's channel notices (`SKIP`), and not a person Slack calls a bot. The
 * model's window (lib/client-slack-prompts.ts) leaves out the same messages.
 */
export function spoken(m: WaitMessage, people: ReadonlyMap<string, WaitPerson>): boolean {
  return m.userId !== null && m.botId === null && !SKIP.has(m.subtype ?? "") && !(people.get(m.userId)?.bot ?? false);
}

export function channelWaits(
  channelId: string,
  messages: readonly WaitMessage[],
  people: ReadonlyMap<string, WaitPerson>,
  since: number,
): Wait[] {
  const person = (u: string | null): WaitPerson | undefined => (u === null ? undefined : people.get(u));
  const human = (m: WaitMessage): boolean => spoken(m, people);
  const yws = (u: string | null): boolean => person(u)?.yws ?? false;

  // Each ts read as a number once, not twice per comparison.
  const ms = messages
    .filter(human)
    .map((m) => ({ m, at: secs(m.ts) }))
    .sort((a, b) => a.at - b.at || (a.m.ts < b.m.ts ? -1 : a.m.ts > b.m.ts ? 1 : 0))
    .map((x) => x.m);
  const threads = new Map<string, WaitMessage[]>();
  for (const m of ms) {
    const key = m.threadTs ?? m.ts;
    const list = threads.get(key);
    if (list) list.push(m);
    else threads.set(key, [m]);
  }
  // The top-level YWS messages that @-mention anyone, each with its mentions,
  // read ONCE: an open wait looks through these, and reading every top-level
  // message's mentions again for every open wait was a page's worth of time
  // on 90 days of channels.
  const answers = ms
    .filter((m) => (m.threadTs === null || m.threadTs === m.ts) && yws(m.userId))
    .map((m) => ({ m, at: secs(m.ts), mentions: mentionsOf(m.text) }))
    .filter((a) => a.mentions.size > 0);

  const out: Wait[] = [];
  threads.forEach((thread, key) => {
    let w: { start: number; msgs: WaitMessage[]; kinds: MessageKind[] } | null = null;
    const close = (end: number | null, by: string | null, how: Wait["endedBy"]) => {
      if (w === null) return;
      out.push(toWait(channelId, key, w.start, w.msgs, w.kinds, end, by, how, yws));
      w = null;
    };
    for (const m of thread) {
      if (!yws(m.userId)) {
        // An old thread's root, kept for context, opens no wait.
        if (secs(m.ts) < since) continue;
        if (w === null) w = { start: secs(m.ts), msgs: [m], kinds: [messageKind(m.text)] };
        else {
          w.msgs.push(m);
          w.kinds.push(messageKind(m.text));
        }
      } else if (w !== null) {
        close(secs(m.ts), m.userId, "thread");
      }
    }
    if (w !== null) {
      const open: { start: number; msgs: WaitMessage[] } = w;
      const who = new Set(open.msgs.map((m) => m.userId as string));
      const answer = answers.find((a) => a.at > open.start && Array.from(a.mentions).some((u) => who.has(u)));
      if (answer) close(answer.at, answer.m.userId, "mention");
      else close(null, null, null);
    }
  });
  return out.sort((a, b) => a.start - b.start);
}

function toWait(
  channelId: string,
  threadTs: string,
  start: number,
  msgs: WaitMessage[],
  kinds: MessageKind[],
  end: number | null,
  answeredBy: string | null,
  endedBy: Wait["endedBy"],
  yws: (u: string | null) => boolean,
): Wait {
  const authors: string[] = [];
  for (const m of msgs) if (m.userId !== null && !authors.includes(m.userId)) authors.push(m.userId);
  return {
    channelId,
    threadTs,
    start,
    end,
    answeredBy,
    endedBy,
    anchors: msgs.map((m) => m.ts),
    authors,
    kinds,
    needs: kinds.some((k) => k !== "ack"),
    acked: msgs.some((m) => (m.reactions ?? []).some((r) => (r.users ?? []).some((u) => yws(u)))),
  };
}
